import * as bodySegmentation from '@tensorflow-models/body-segmentation'

const createSegmenter = async () => {
  const model = bodySegmentation.SupportedModels.MediaPipeSelfieSegmentation
  const segmenterConfig = {
    runtime: 'mediapipe',
    solutionPath: 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation',
    modelType: 'general',
  }
  return bodySegmentation.createSegmenter(model, segmenterConfig)
}

class VideoBackground {
  #animationId
  #segmenter

  getSegmenter = async () => {
    if (!this.#segmenter) {
      this.#segmenter = await createSegmenter()
    }
    return this.#segmenter
  }

  stop = () => {
    if (this.#animationId) {
      cancelAnimationFrame(this.#animationId)
      this.#animationId = null
    }
  }

  blur = async (canvas, video) => {
    const context = canvas.getContext('2d')
    const segmenter = await this.getSegmenter()
    /*
    console.log(
      'videoBg:blurBg -> video size',
      video.videoWidth,
      video.videoHeight,
    )
    */

    const processFrame = async () => {
      try {
        // First, draw the video normally
        context.drawImage(video, 0, 0)

        // Get the segmentation mask
        const segmentation = await segmenter.segmentPeople(video)
        const coloredPartImage =
          await bodySegmentation.toBinaryMask(segmentation)

        // Scale the segmentation mask to match canvas dimensions
        const scaledMask = document.createElement('canvas')
        scaledMask.width = canvas.width
        scaledMask.height = canvas.height
        const maskContext = scaledMask.getContext('2d')

        // Create a temporary canvas to hold the original mask
        const tempMaskCanvas = document.createElement('canvas')
        tempMaskCanvas.width = coloredPartImage.width
        tempMaskCanvas.height = coloredPartImage.height
        const tempMaskContext = tempMaskCanvas.getContext('2d')
        tempMaskContext.putImageData(coloredPartImage, 0, 0)

        // Scale the mask to canvas dimensions
        maskContext.drawImage(
          tempMaskCanvas,
          0,
          0,
          coloredPartImage.width,
          coloredPartImage.height,
          0,
          0,
          canvas.width,
          canvas.height,
        )
        const scaledMaskData = maskContext.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        )

        // Create a temporary canvas for the blurred background
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = canvas.width
        tempCanvas.height = canvas.height
        const tempContext = tempCanvas.getContext('2d')

        // Draw the video scaled to fill the entire canvas
        tempContext.drawImage(video, 0, 0, canvas.width, canvas.height)

        // Apply blur filter to the entire temp canvas
        tempContext.filter = 'blur(25px)'
        tempContext.drawImage(tempCanvas, 0, 0)

        // Get the image data from both canvases
        const originalImageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        )
        const blurredImageData = tempContext.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        )

        // Create the final image data by combining original (person) and blurred (background)
        const finalImageData = context.createImageData(
          canvas.width,
          canvas.height,
        )

        for (let i = 0; i < originalImageData.data.length; i += 4) {
          const pixelIndex = i / 4
          const alphaIndex = pixelIndex * 4 + 3

          // If this is a background pixel (alpha = 255 in mask), use blurred version
          if (scaledMaskData.data[alphaIndex] === 255) {
            finalImageData.data[i] = blurredImageData.data[i] // R
            finalImageData.data[i + 1] = blurredImageData.data[i + 1] // G
            finalImageData.data[i + 2] = blurredImageData.data[i + 2] // B
            finalImageData.data[i + 3] = originalImageData.data[i + 3] // A
          } else {
            // If this is a person pixel, use original version
            finalImageData.data[i] = originalImageData.data[i]
            finalImageData.data[i + 1] = originalImageData.data[i + 1]
            finalImageData.data[i + 2] = originalImageData.data[i + 2]
            finalImageData.data[i + 3] = originalImageData.data[i + 3]
          }
        }

        // Draw the final result
        context.putImageData(finalImageData, 0, 0)
      } catch (error) {
        console.warn('videoBg: Error in blur processing:', error)
      }

      if (this.#animationId) {
        this.#animationId = requestAnimationFrame(processFrame)
      }
    }
    this.#animationId = requestAnimationFrame(processFrame)
  }

  remove = async (canvas, video) => {
    const context = canvas.getContext('2d')
    const segmenter = await this.getSegmenter()
    /*
    console.log(
      'videoBg:removeBg -> video size',
      video.videoWidth,
      video.videoHeight,
    )
    */

    const processFrame = async () => {
      try {
        // Draw video scaled to canvas dimensions
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        const segmentation = await segmenter.segmentPeople(video)
        const coloredPartImage =
          await bodySegmentation.toBinaryMask(segmentation)

        // Scale the segmentation mask to match canvas dimensions
        const scaledMask = document.createElement('canvas')
        scaledMask.width = canvas.width
        scaledMask.height = canvas.height
        const maskContext = scaledMask.getContext('2d')

        // Create a temporary canvas to hold the original mask
        const tempMaskCanvas = document.createElement('canvas')
        tempMaskCanvas.width = coloredPartImage.width
        tempMaskCanvas.height = coloredPartImage.height
        const tempMaskContext = tempMaskCanvas.getContext('2d')
        tempMaskContext.putImageData(coloredPartImage, 0, 0)

        // Scale the mask to canvas dimensions
        maskContext.drawImage(
          tempMaskCanvas,
          0,
          0,
          coloredPartImage.width,
          coloredPartImage.height,
          0,
          0,
          canvas.width,
          canvas.height,
        )
        const scaledMaskData = maskContext.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        )

        const imageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        )
        // [R,G,B,A,R,G,B,A...]. below for loop iterate through alpha channel
        for (let i = 3; i < imageData.data.length; i += 4) {
          // By default background pixels alpha will be 255.
          if (scaledMaskData.data[i] === 255) {
            imageData.data[i] = 0 // this is a background pixel's alpha. Make it fully transparent
          }
        }
        await bodySegmentation.drawMask(canvas, imageData)
      } catch (error) {
        console.warn('videoBg: Error in remove processing:', error)
      }

      if (this.#animationId) {
        this.#animationId = requestAnimationFrame(processFrame)
      }
    }
    this.#animationId = requestAnimationFrame(processFrame)
  }

  // Method to check if background effects are supported
  isSupported = () => {
    return (
      typeof bodySegmentation !== 'undefined' &&
      bodySegmentation.SupportedModels?.MediaPipeSelfieSegmentation
    )
  }
}

const videoBackground = new VideoBackground()

export default videoBackground
